<script>
  import { format_date, format_utc_time_as_local, convert_local_time_to_utc } from '#lib/util.js';

  let { 
    is_editing, 
    value: utc_timestamp = $bindable(), // UTC ISO string (e.g. "2024-03-14T10:00:00Z")
    onchange,
    min,
    max,
    step = 60,
    variant = 'brand subtle-outlined',
    class: css_class,
    ...rest 
  } = $props();

  let local_time_str = $state(''); // Local time in HH:mm format
  let is_input_focused = $state(false);

  /**
   * Converts UTC timestamp to local time string (HH:mm) when the UTC value changes
   * Example: "2024-03-14T10:00:00Z" -> "12:00" (in user's timezone)
   * 
   * In detail: We use a separate local_time_str state instead of binding directly to value because:
   * 1. The 'value' prop is in UTC format (e.g. "2024-03-14T10:00:00Z")
   * 2. The time input expects local time in HH:mm format
   * 3. We need to handle timezone conversions between UTC and local time
   */
  $effect(() => {
    if (utc_timestamp) {
      local_time_str = format_utc_time_as_local(utc_timestamp);
    }
  });

  /**
   * Converts local time back to UTC when input changes, preserving the original date
   * Example: If original was "2024-03-14T10:00:00Z" and time changes to "15:30",
   * result will be "2024-03-14T13:30:00Z"
   * 
   * In detail: The submit_update function handles:
   * 1. Converting local time back to UTC while preserving the date
   * 2. Debouncing updates during focus to prevent partial input issues
   * 3. Triggering the onchange callback with the new UTC value
   * 
   * We can't use a direct two-way binding because:
   * - We need to coordinate between focus state and value updates
   * - The conversion between UTC and local time needs to happen at specific points
   * - We want to preserve the date portion of the original UTC timestamp
   */
  function submit_update() {
    if (is_input_focused || !local_time_str) return;
    
    utc_timestamp = convert_local_time_to_utc(local_time_str, utc_timestamp);
    
    if (onchange) {
      onchange(utc_timestamp);
    }
  }
</script>

{#if is_editing}
  <input
    type="time"
    value={local_time_str}
    onchange={(e) => {
      local_time_str = e.target.value;
      submit_update();
    }}
    onblur={() => {
      is_input_focused = false;
      submit_update();
    }}
    onfocus={() => is_input_focused = true}
    class="text-control {css_class} {variant} {variant.includes('outlined') ? variant.includes('system') ? 'border-1-system' : 'border-1' : ''}"
    {step}
    {min}
    {max}
    {...rest}
  />
{:else}
  {#if utc_timestamp}
    {#key utc_timestamp}
      {format_date(utc_timestamp, 'Hm')}
    {/key}
  {:else}
    --:--
  {/if}
{/if}

<style lang='postcss'>
  input[type=time] {
    width: fit-content;
  }
</style>